import { ReactNode } from 'react';
import { Link, useLocation, type LinkProps } from 'react-router-dom';
import { useLayoutTier } from '../hooks/useLayoutTier';
import { cn } from '../utils/className';

interface NavigationItemProps extends Omit<
  LinkProps,
  'to' | 'children' | 'title'
> {
  route: string;
  title: string | ReactNode;
  icon?: ReactNode;
}

export function NavigationItem({
  route,
  title,
  icon,
  className,
  ...rest
}: NavigationItemProps) {
  const location = useLocation();
  const isActive = location.pathname === route;
  const { isTabletDown } = useLayoutTier();

  const baseClasses = isTabletDown
    ? 'text-primary hover:bg-surface-primary-support rounded-lg px-4 gap-4'
    : 'text-inverted hover:bg-white/10 px-6 gap-3';

  const activeClasses = isTabletDown
    ? 'text-focus bg-surface-primary-support'
    : 'text-inverted bg-white/10';

  return (
    <li>
      <Link
        to={route}
        className={cn(
          'relative flex h-12 items-center py-3 no-underline transition-colors',
          baseClasses,
          isActive && activeClasses,
          className
        )}
        {...rest}
      >
        {isActive && !isTabletDown && (
          <span
            className='bg-inverted absolute top-2 left-0 h-8 w-1 rounded-r-sm'
          />
        )}
        {icon ? (
          <span
            className={cn(
              `flex shrink-0 items-center justify-center [&_path]:fill-current
                [&_svg]:fill-current`,
              isTabletDown ? 'h-5 w-5' : 'h-4 w-4'
            )}
          >
            {icon}
          </span>
        ) : null}

        <span className={cn('flex-1', isTabletDown ? 'size-p1' : 'size-p2')}>
          {title}
        </span>
      </Link>
    </li>
  );
}
